import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://iia-backend-production.up.railway.app';

const Apply = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone: '',
    profession: '',
    why_joining: '',
    goals: '',
    heard_about: '',
  });

  const update = (field, value) => setForm({ ...form, [field]: value });

  const validateStep1 = () => {
    if (!form.first_name || !form.last_name) return 'Please enter your full name.';
    if (!form.email) return 'Please enter your email.';
    if (!form.password || form.password.length < 8) return 'Password must be at least 8 characters.';
    if (form.password !== form.confirm_password) return 'Passwords do not match.';
    return null;
  };

  const validateStep2 = () => {
    if (!form.profession) return 'Please enter your profession.';
    if (!form.why_joining) return 'Please tell us why you want to join.';
    if (!form.goals) return 'Please share your goals.';
    return null;
  };

  const handleNext = () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    const err = validateStep2();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admissions/apply`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✅</div>
          <h2 style={styles.successTitle}>Application Submitted</h2>
          <p style={styles.successText}>
            Thank you, {form.first_name}. Your application is under review.
            You'll receive an email at <strong>{form.email}</strong> once
            a decision has been made.
          </p>
          <p style={styles.successSubtext}>
            This typically takes 1–2 business days.
          </p>
          <button style={styles.loginBtn} onClick={() => navigate('/')}>
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.logo}>IIA</h1>
          <p style={styles.logoSub}>Impact Influence Academy</p>
          <h2 style={styles.title}>Apply for Admission</h2>
          <p style={styles.subtitle}>
            No-filler 16-week professional hypnosis certification
          </p>
        </div>

        {/* Step Indicator */}
        <div style={styles.stepIndicator}>
          <div style={step >= 1 ? styles.stepActive : styles.step}>
            <span style={styles.stepNum}>1</span>
            <span style={styles.stepLabel}>Account</span>
          </div>
          <div style={styles.stepLine} />
          <div style={step >= 2 ? styles.stepActive : styles.step}>
            <span style={styles.stepNum}>2</span>
            <span style={styles.stepLabel}>Background</span>
          </div>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Step 1 — Account Info */}
        {step === 1 && (
          <div style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>First Name</label>
                <input
                  style={styles.input}
                  placeholder="First name"
                  value={form.first_name}
                  onChange={e => update('first_name', e.target.value)}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Last Name</label>
                <input
                  style={styles.input}
                  placeholder="Last name"
                  value={form.last_name}
                  onChange={e => update('last_name', e.target.value)}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                style={styles.input}
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={e => update('email', e.target.value)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone (optional)</label>
              <input
                style={styles.input}
                placeholder="Phone number"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Create Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="Minimum 8 characters"
                value={form.password}
                onChange={e => update('password', e.target.value)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="Repeat your password"
                value={form.confirm_password}
                onChange={e => update('confirm_password', e.target.value)}
              />
            </div>

            <button style={styles.nextBtn} onClick={handleNext}>
              Continue →
            </button>

            <p style={styles.loginPrompt}>
              Already have an account?{' '}
              <span style={styles.loginLink} onClick={() => navigate('/')}>
                Log in
              </span>
            </p>
          </div>
        )}

        {/* Step 2 — Background */}
        {step === 2 && (
          <div style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Your Profession / Field</label>
              <input
                style={styles.input}
                placeholder="e.g. Therapist, Sales Professional, Coach"
                value={form.profession}
                onChange={e => update('profession', e.target.value)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Why do you want to join IIA?</label>
              <textarea
                style={styles.textarea}
                placeholder="Tell us what brought you here and what you're hoping to achieve..."
                value={form.why_joining}
                onChange={e => update('why_joining', e.target.value)}
                rows={4}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>What are your goals?</label>
              <textarea
                style={styles.textarea}
                placeholder="What do you want to be able to do after completing this program?"
                value={form.goals}
                onChange={e => update('goals', e.target.value)}
                rows={4}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>How did you hear about us? (optional)</label>
              <input
                style={styles.input}
                placeholder="e.g. Social media, referral, Google..."
                value={form.heard_about}
                onChange={e => update('heard_about', e.target.value)}
              />
            </div>

            <div style={styles.formActions}>
              <button style={styles.backBtn} onClick={() => { setStep(1); setError(''); }}>
                ← Back
              </button>
              <button
                style={loading ? styles.submitBtnDisabled : styles.submitBtn}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  card: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '16px',
    padding: '48px',
    width: '100%',
    maxWidth: '560px',
  },
  header: { textAlign: 'center', marginBottom: '32px' },
  logo: { color: '#C49A28', fontSize: '32px', fontWeight: '800', margin: '0', letterSpacing: '3px' },
  logoSub: { color: '#ffffff40', fontSize: '12px', letterSpacing: '2px', margin: '4px 0 24px 0', textTransform: 'uppercase' },
  title: { color: '#ffffff', fontSize: '22px', fontWeight: '700', margin: '0 0 8px 0' },
  subtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  stepIndicator: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '32px', gap: '0' },
  step: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', opacity: 0.4 },
  stepActive: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  stepNum: { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#C49A28', color: '#1A2744', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700' },
  stepLabel: { color: '#ffffff', fontSize: '12px', fontWeight: '600' },
  stepLine: { width: '60px', height: '2px', backgroundColor: '#ffffff20', margin: '0 8px', marginBottom: '16px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { color: '#ffffff80', fontSize: '13px', fontWeight: '600' },
  input: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '8px', padding: '12px 14px', color: '#ffffff', fontSize: '14px', outline: 'none', boxSizing: 'border-box', width: '100%' },
  textarea: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '8px', padding: '12px 14px', color: '#ffffff', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', width: '100%' },
  errorBox: { backgroundColor: '#ff000015', border: '1px solid #ff000040', borderRadius: '8px', padding: '12px 16px', color: '#ff6666', fontSize: '13px', marginBottom: '8px' },
  nextBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', width: '100%', marginTop: '8px' },
  formActions: { display: 'flex', gap: '12px', marginTop: '8px' },
  backBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff30', color: '#ffffff60', borderRadius: '8px', padding: '14px 20px', fontSize: '14px', cursor: 'pointer' },
  submitBtn: { flex: 1, backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  submitBtnDisabled: { flex: 1, backgroundColor: '#C49A2860', color: '#1A274480', border: 'none', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '700', cursor: 'not-allowed' },
  loginPrompt: { textAlign: 'center', color: '#ffffff40', fontSize: '13px', margin: '8px 0 0 0' },
  loginLink: { color: '#C49A28', cursor: 'pointer', fontWeight: '600' },
  successIcon: { fontSize: '48px', textAlign: 'center', marginBottom: '16px' },
  successTitle: { color: '#C49A28', fontSize: '24px', fontWeight: '700', textAlign: 'center', margin: '0 0 16px 0' },
  successText: { color: '#ffffffcc', fontSize: '15px', lineHeight: '1.7', textAlign: 'center', margin: '0 0 8px 0' },
  successSubtext: { color: '#ffffff60', fontSize: '13px', textAlign: 'center', margin: '0 0 32px 0' },
  loginBtn: { width: '100%', backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
};

export default Apply;